import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Lightbulb, ArrowRight, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { AddrLink, Card, CopyBtn, DetailRow, ErrorState, SkeletonRows, StatusBadge, TokenLogo, TypeBadge } from "@/components/common";
import { fmtDate, fmtEth, fmtNum, fmtTokenAmount, fmtUsd, timeAgo, weiToEth, shortHash } from "@/lib/format";
import { usePageTitle } from "@/lib/seo";

export default function TxPage() {
  const { hash } = useParams();
  const [tx, setTx] = useState(null);
  const [err, setErr] = useState(false);
  const [logs, setLogs] = useState(null);
  const [showLogs, setShowLogs] = useState(false);
  usePageTitle(`Transaction ${shortHash(hash)}`, `Details, plain-language summary and token movements for transaction ${hash} on Robinhood Chain.`);

  useEffect(() => {
    setTx(null); setErr(false); setLogs(null); setShowLogs(false);
    api.get(`/transactions/${hash}`).then((r) => setTx(r.data)).catch(() => setErr(true));
  }, [hash]);

  const loadLogs = () => {
    setShowLogs(!showLogs);
    if (!logs) api.get(`/transactions/${hash}/logs`).then((r) => setLogs(r.data.items || [])).catch(() => setLogs([]));
  };

  if (err) return <ErrorState message="We could not find this transaction on Robinhood Chain. Please check the value and try again." />;
  if (!tx) return <SkeletonRows n={8} h="h-14" />;

  const ethUsd = tx.exchange_rate ? Number(tx.exchange_rate) : null;
  const feeEth = weiToEth(tx.fee?.value);

  return (
    <div className="space-y-6 pt-8 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Transaction Details</h1>
        <div className="mt-1 flex items-center gap-2">
          <p className="font-mono text-xs text-muted-foreground break-all" data-testid="tx-hash">{hash}</p>
          <CopyBtn text={hash} testId="copy-tx-btn" />
        </div>
      </div>

      <Card className="p-4 sm:p-5 border-primary/30 bg-primary/5" data-testid="tx-summary">
        <div className="flex items-start gap-3">
          <Lightbulb size={16} className="text-primary mt-0.5 shrink-0" />
          <div>
            <p className="text-[10px] uppercase tracking-wider text-primary font-semibold">What happened</p>
            <p className="mt-1 text-sm sm:text-base leading-relaxed">{tx.summary}</p>
          </div>
        </div>
      </Card>

      <Card className="px-4 sm:px-5">
        <DetailRow label="Status" testId="tx-status"><StatusBadge status={tx.status} />{tx.revert_reason ? <span className="ml-2 text-xs text-destructive">{tx.revert_reason}</span> : null}</DetailRow>
        <DetailRow label="Block"><Link to={`/block/${tx.block_number}`} className="text-primary hover:underline font-mono text-xs">#{tx.block_number}</Link>
          <span className="ml-2 text-xs text-muted-foreground">{tx.confirmations ? `${fmtNum(tx.confirmations, { maximumFractionDigits: 0 })} confirmations` : ""}</span></DetailRow>
        <DetailRow label="Timestamp">{fmtDate(tx.timestamp)} <span className="text-xs text-muted-foreground">({timeAgo(tx.timestamp)})</span></DetailRow>
        <DetailRow label="From"><span className="inline-flex items-center gap-2"><AddrLink hash={tx.from?.hash} chars={10} /> {tx.from?.gmscan_label && <TypeBadge>{tx.from.gmscan_label}</TypeBadge>} <CopyBtn text={tx.from?.hash || ""} /></span></DetailRow>
        <DetailRow label="To"><span className="inline-flex items-center gap-2"><ArrowRight size={11} className="text-muted-foreground" /><AddrLink hash={tx.to?.hash || tx.created_contract?.hash} name={tx.to?.name} chars={10} /> {tx.to?.gmscan_label && <TypeBadge>{tx.to.gmscan_label}</TypeBadge>}{tx.to?.hash && <CopyBtn text={tx.to.hash} />}</span></DetailRow>
        <DetailRow label="Value" testId="tx-value">{fmtEth(tx.value)} {ethUsd && weiToEth(tx.value) > 0 ? <span className="text-xs text-muted-foreground">({fmtUsd(weiToEth(tx.value) * ethUsd)})</span> : null}</DetailRow>
        <DetailRow label="Transaction Fee">{fmtEth(tx.fee?.value, 10)} {ethUsd ? <span className="text-xs text-muted-foreground">({fmtUsd(feeEth * ethUsd, 6)})</span> : null}</DetailRow>
        <DetailRow label="Gas Used">{fmtNum(tx.gas_used, { maximumFractionDigits: 0 })} <span className="text-xs text-muted-foreground">of {fmtNum(tx.gas_limit, { maximumFractionDigits: 0 })}</span></DetailRow>
        <DetailRow label="Gas Price">{tx.gas_price ? `${(Number(tx.gas_price) / 1e9).toFixed(4)} gwei` : "—"}</DetailRow>
        <DetailRow label="Contract Method">{tx.method ? <code className="text-xs bg-secondary rounded px-2 py-0.5">{tx.method}</code> : "—"}</DetailRow>
        <DetailRow label="Type">{(tx.transaction_types || []).map((t) => <TypeBadge key={t}>{t.replace(/_/g, " ")}</TypeBadge>)}</DetailRow>
      </Card>

      {tx.token_transfers?.length > 0 && (
        <Card data-testid="tx-token-transfers">
          <h2 className="px-4 pt-4 pb-2 text-base font-semibold">Token Movements</h2>
          <div className="divide-y">
            {tx.token_transfers.map((t, i) => (
              <div key={i} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm">
                <TokenLogo url={t.token?.icon_url} symbol={t.token?.symbol} size={22} />
                <span className="font-mono text-xs">{fmtTokenAmount(t.total?.value, t.total?.decimals ?? t.token?.decimals)}</span>
                <Link to={`/token/${t.token?.address_hash}`} className="text-primary hover:underline text-xs">{t.token?.symbol || t.token?.name}</Link>
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  <AddrLink hash={t.from?.hash} chars={4} /> <ArrowRight size={10} /> <AddrLink hash={t.to?.hash} chars={4} />
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <button data-testid="toggle-logs-btn" onClick={loadLogs} className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold">
          Event Logs <ChevronDown size={14} className={`transition-transform duration-200 ${showLogs ? "rotate-180" : ""}`} />
        </button>
        {showLogs && (
          logs === null ? <SkeletonRows n={3} /> : logs.length === 0 ? (
            <p className="px-4 pb-4 text-xs text-muted-foreground">No event logs for this transaction.</p>
          ) : (
            <div className="divide-y border-t">
              {logs.map((l, i) => (
                <div key={i} className="px-4 py-3 text-xs space-y-1">
                  <p><span className="text-muted-foreground">Contract:</span> <AddrLink hash={l.address?.hash} name={l.address?.name} chars={8} /></p>
                  {l.decoded?.method_call && <p className="font-mono break-all text-primary">{l.decoded.method_call}</p>}
                  <p className="font-mono break-all text-muted-foreground">{(l.topics || []).filter(Boolean).join(" · ")}</p>
                </div>
              ))}
            </div>
          )
        )}
      </Card>
    </div>
  );
}
